import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import './GatePass.css';

function GatePass({
  visitor,
  onNewRegistration,
  onViewRecords,
  onClose,
  onToggleStatus,
  isModal = false,
}) {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const passRef = useRef(null);

  // Generate fallback pass number if visitor has none stored
  const passNumber =
    visitor.passNumber ||
    `GP-${new Date(visitor.visitDate || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}-${(visitor._id || '').slice(-4).toUpperCase() || '0001'}`;

  const passStatus = visitor.passStatus || 'active';
  const isInactive = passStatus === 'inactive';
  const isCheckedOut = Boolean(visitor.checkOutTime);

  // Format date nicely
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Generate QR Code data URL
  useEffect(() => {
    const payload = JSON.stringify({
      passNo: passNumber,
      id: visitor._id || '',
      name: visitor.name,
      phone: visitor.phone || '',
      aadhar: visitor.aadharNumber || '',
      purpose: visitor.purposeOfVisit,
      whomToMeet: visitor.whomToMeet || '',
      checkIn: visitor.visitDate,
      passStatus: passStatus,
      validDate: new Date(visitor.visitDate || Date.now()).toLocaleDateString(),
    });

    QRCode.toDataURL(payload, {
      width: 160,
      margin: 1,
      color: {
        dark: isInactive ? '#991b1b' : '#1e293b',
        light: '#ffffff',
      },
    })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => console.error('Error generating QR code:', err));
  }, [visitor, passNumber, passStatus, isInactive]);

  // Handle printing
  const handlePrint = () => {
    window.print();
  };

  // Get initials for avatar badge
  const initials = visitor.name
    ? visitor.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
    : 'V';

  const [isUpdating, setIsUpdating] = useState(false);

  const handleDeactivate = async () => {
    if (!onToggleStatus || isUpdating) return;
    if (isInactive) {
      alert('This gate pass has already been deactivated and cannot be reactivated.');
      return;
    }
    setIsUpdating(true);
    try {
      await onToggleStatus(visitor._id, 'inactive');
    } finally {
      setIsUpdating(false);
    }
  };

  const passContent = (
    <div className={`gatepass-wrapper ${isInactive ? 'wrapper-inactive' : ''}`}>
      {/* Top Banner / Actions Bar (Screen only) */}
      <div className="gatepass-actions-bar no-print">
        <div className="actions-left">
          <span className={`status-summary-tag ${isInactive ? 'tag-inactive' : 'tag-active'}`}>
            {isInactive ? '⚠️ Pass Inactive / Revoked' : '✓ Pass Active'}
          </span>
          <span className="actions-subtitle">
            Pass No: <strong>{passNumber}</strong> | Visitor: <strong>{visitor.name}</strong>
          </span>
        </div>
        <div className="actions-buttons">
          {/* Deactivate Action - Permanent, only shown when pass is active */}
          {onToggleStatus && !isInactive && (
            <button
              className="btn-action btn-deactivate"
              onClick={handleDeactivate}
              disabled={isUpdating}
              title="Deactivate this gate pass permanently"
            >
              <span className="btn-icon">🔴</span>
              {isUpdating ? 'Deactivating...' : 'Deactivate Pass'}
            </button>
          )}

          {isInactive && (
            <span
              className="badge-permanently-inactive"
              title="Once deactivated, a gate pass cannot be reactivated"
            >
              🚫 Deactivated
            </span>
          )}

          <button className="btn-action btn-print" onClick={handlePrint}>
            <span className="btn-icon">🖨️</span> Print Pass
          </button>

          {onNewRegistration && (
            <button className="btn-action btn-secondary" onClick={onNewRegistration}>
              <span className="btn-icon">➕</span> New Registration
            </button>
          )}

          {onViewRecords && (
            <button className="btn-action btn-tertiary" onClick={onViewRecords}>
              <span className="btn-icon">📋</span> View Records
            </button>
          )}

          {onClose && (
            <button className="btn-action btn-close" onClick={onClose} title="Close">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* The Printable Security Gate Pass Badge */}
      <div className={`gatepass-card ${isInactive ? 'card-inactive' : ''}`} ref={passRef} id="printable-gatepass">
        {/* Pass Header */}
        <div className={`gatepass-header ${isInactive ? 'header-inactive' : ''}`}>
          <div className="gatepass-header-top">
            <div className="organization-brand">
              <div className="pass-logos-group">
                <div className="pass-logo-pill">
                  <img
                    src="/logos/PMC_logo.png"
                    alt="PMC Logo"
                    className="pass-brand-img logo-pmc"
                    onError={(e) => {
                      e.target.src = '/api/visitors/logo/PMC_logo.png';
                    }}
                  />
                </div>
                <div className="pass-logo-pill">
                  <img
                    src="/logos/Innover_logo.png"
                    alt="Innover Logo"
                    className="pass-brand-img logo-innover"
                    onError={(e) => {
                      e.target.src = '/api/visitors/logo/Innover_logo.png';
                    }}
                  />
                </div>
              </div>
              <div className="brand-text">
                <span className="brand-title">VISITOR GATE PASS</span>
                <span className="brand-subtitle">ICCC Security</span>
              </div>
            </div>
            {isInactive ? (
              <div className="pass-status-badge status-inactive">
                <span className="status-dot status-dot-inactive"></span>
                INACTIVE / VOID
              </div>
            ) : (
              <div className={`pass-status-badge ${isCheckedOut ? 'status-checkout' : 'status-active'}`}>
                <span className="status-dot"></span>
                {isCheckedOut ? 'CHECKED OUT' : 'ACTIVE PASS'}
              </div>
            )}
          </div>
          <div className="pass-number-banner">
            <div className="banner-left">
              <span className="pass-number-label">PASS NUMBER</span>
              <span className="pass-number-code">{passNumber}</span>
            </div>
            {visitor._id && (
              <div className="banner-right">
                <span className="user-id-label">VISITOR ID</span>
                <span className="user-id-code">{visitor._id.slice(-8).toUpperCase()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Inactive Warning Ribbon */}
        {isInactive && (
          <div className="inactive-alert-banner">
            ⚠️ THIS GATE PASS IS INACTIVE — ACCESS NOT PERMITTED
          </div>
        )}

        {/* Pass Body */}
        <div className="gatepass-body">
          {/* Visitor Primary Info */}
          <div className="visitor-primary-section">
            <div className={`visitor-avatar-badge ${visitor.photoUrl || visitor.photoData ? 'has-photo' : ''} ${isInactive ? 'avatar-inactive' : ''}`}>
              {visitor.photoUrl || visitor.photoData ? (
                <img
                  src={
                    visitor.photoData ||
                    (visitor._id ? `/api/visitors/${visitor._id}/photo` : visitor.photoUrl)
                  }
                  alt={visitor.name}
                  className="visitor-badge-photo"
                  onError={(e) => {
                    if (visitor.photoUrl && e.target.src !== visitor.photoUrl) {
                      e.target.src = visitor.photoUrl;
                    } else {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.visitor-initials-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <span
                className="visitor-initials-fallback"
                style={{ display: visitor.photoUrl || visitor.photoData ? 'none' : 'flex' }}
              >
                {initials}
              </span>
            </div>
            <div className="visitor-name-block">
              <h2 className="visitor-name-title">{visitor.name}</h2>
              <div className="visitor-affiliation">
                {visitor.company ? (
                  <span className="affiliation-company">🏢 {visitor.company}</span>
                ) : (
                  <span className="affiliation-independent">Individual / Guest</span>
                )}
                <span className={`purpose-tag purpose-${(visitor.purposeOfVisit || 'other').toLowerCase()}`}>
                  {visitor.purposeOfVisit}
                </span>
                <span className={`pass-status-tag ${isInactive ? 'tag-pill-inactive' : 'tag-pill-active'}`}>
                  Status: {isInactive ? 'Inactive' : 'Active'}
                </span>
              </div>
            </div>
          </div>

          {/* Details Grid & QR Verification */}
          <div className="gatepass-details-row">
            <div className="gatepass-info-grid">
              <div className="pass-field">
                <span className="field-label">Phone Number</span>
                <span className="field-value">{visitor.phone || '—'}</span>
              </div>
              <div className="pass-field">
                <span className="field-label">Email Address</span>
                <span className="field-value">{visitor.email || '—'}</span>
              </div>
              {visitor.aadharNumber && (
                <div className="pass-field">
                  <span className="field-label">Aadhaar Number</span>
                  <span className="field-value highlight">{visitor.aadharNumber}</span>
                </div>
              )}
              {visitor.whomToMeet && (
                <div className="pass-field">
                  <span className="field-label">Whom to Meet</span>
                  <span className="field-value highlight">{visitor.whomToMeet}</span>
                </div>
              )}

              <div className="pass-field">
                <span className="field-label">Check-In Time</span>
                <span className="field-value highlight">{formatDateTime(visitor.visitDate)}</span>
              </div>
              {visitor.checkOutTime && (
                <div className="pass-field">
                  <span className="field-label">Check-Out Time</span>
                  <span className="field-value">{formatDateTime(visitor.checkOutTime)}</span>
                </div>
              )}
              {/* {visitor.notes && (
                <div className="pass-field field-full">
                  <span className="field-label">Notes / Host Info</span>
                  <span className="field-value notes-text">{visitor.notes}</span>
                </div>
              )} */}
            </div>

            {/* QR Code Scannable Area */}
            <div className={`gatepass-qr-container ${isInactive ? 'qr-inactive' : ''}`}>
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="Visitor Verification QR Code" className="qr-image" />
              ) : (
                <div className="qr-loading">Generating QR...</div>
              )}
              <span className="qr-caption">
                {isInactive ? 'Pass Deactivated' : 'Scan for security verification'}
              </span>
            </div>
          </div>

          {/* Security Notice & Signature Row */}
          <div className="gatepass-security-footer">
            <div className="security-instructions">
              <div className="instruction-item">
                <span className="bullet">✦</span> Valid for authorized date & zones only.
              </div>
              <div className="instruction-item">
                <span className="bullet">✦</span> Must be worn visibly around neck or pinned at all times.
              </div>
              <div className="instruction-item">
                <span className="bullet">✦</span> Please surrender this badge at security desk upon checkout.
              </div>
            </div>

            <div className="signatures-container">
              <div className="signature-box">
                <div className="signature-line"></div>
                <span className="signature-label">Visitor Signature</span>
              </div>
              <div className="signature-box">
                <div className="signature-line"></div>
                <span className="signature-label">Authorized Officer</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative security stripe */}
        <div className={`gatepass-hologram-strip ${isInactive ? 'strip-inactive' : ''}`}>
          <span>
            {isInactive
              ? 'VOID • INACTIVE GATE PASS • ACCESS REVOKED • INACTIVE GATE PASS • VOID'
              : 'OFFICIAL VISITOR PASS • SECURE ACCESS • AUTHORIZED ENTRY • SECURE ACCESS • OFFICIAL VISITOR PASS'}
          </span>
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="gatepass-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
        <div className="gatepass-modal-content">{passContent}</div>
      </div>
    );
  }

  return passContent;
}

export default GatePass;
